import React, { useRef, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Subject } from "rxjs";
import {
  filter,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  tap,
} from "rxjs/operators";
import {
  getCardsIsCompact,
  getCardsIsOnSide,
  getCardsIsViewAll,
  getSelectedCard,
} from "../../../state/selectors/CardsUI.selectors";
import { setSelectedCard } from "../../../state/actions/Cards";
import { eventTrack, smoothScroll } from "../../../common/helpers/helpers";
import { ANALYTICS_CATEGORIES } from "../common/constants";
import Card from "../Card";

function CardGridEl({
  selectedCard,
  selectCard,
  children,
  id,
  isCardsOnSide,
  isViewAll,
  card,
  column,
  row,
  disabled,
}) {
  const isFocused$ = useRef(new Subject());
  const cardRef = useRef(null);
  const isSelected = selectedCard === id;
  const handleOnFocus = useCallback(() => isFocused$.current.next(true), []);
  const handleOnBlur = useCallback(() => isFocused$.current.next(false), []);

  function scrollToComponent(forceScrollId = null) {
    const compId = `${forceScrollId ?? id}__comp`;
    const startPointId = isViewAll ? compId : "components-start";
    const cardComp = document.getElementById(startPointId);
    const offsetTop = cardComp?.offsetTop || 0;
    const current = window.scrollY;
    if (!cardComp) {
      return;
    }

    if (isViewAll && !isCardsOnSide) {
      const padding = 60;
      const headerCont = document.getElementById("cardsContainer");
      const headerHeight = headerCont?.offsetHeight || 0;
      const diff = headerHeight - padding;
      const top = offsetTop > diff ? offsetTop + diff : offsetTop;
      smoothScroll(current, top, 600, 5, window.innerHeight * 0.7);
    } else {
      smoothScroll(current, offsetTop, 500, 1, Infinity);
    }
  }

  const forceScrollToId = ({ detail }) => {
    if (isViewAll) {
      scrollToComponent(detail);
    }
  };

  useEffect(() => {
    document.addEventListener("forceScrollToComponent", forceScrollToId);
    return () => {
      document.removeEventListener("forceScrollToComponent", forceScrollToId);
    };
  }, []);

  function getActiveCard(el) {
    const [cardEl] = el?.children || [];
    return cardEl && Number(cardEl.getAttribute("tabindex")) > -1
      ? cardEl
      : null;
  }
  const handleClick = () => {
    if (getActiveCard(cardRef.current)) {
      if (
        __IS_COMPONENTS_PORTAL__ &&
        selectedCard === id &&
        !selectedCard.includes("proteinViewer")
      )
        return;
      selectCard(id);
      card.onSelect();
      if (!card.disableScroll) {
        scrollToComponent();
      }
    }
  };

  useEffect(() => {
    const destroyed$ = new Subject();
    isFocused$.current
      .pipe(
        takeUntil(destroyed$),
        debounceTime(750),
        distinctUntilChanged(),
        filter((isFocused) => isFocused && !isSelected),
        tap(() =>
          eventTrack(
            ANALYTICS_CATEGORIES.CARDS_UI,
            "Card Selection",
            card.title
          )
        ),
        tap(() => handleClick())
      )
      .subscribe();

    return () => destroyed$.next();
  }, [isSelected]);

  useEffect(() => {
    const activeCard = getActiveCard(cardRef.current);
    if (document.activeElement.classList.contains("cards-on-clinical")) {
      if (isSelected && activeCard) {
        setTimeout(() => activeCard.focus(), 0);
      }
      if (isSelected && isCardsOnSide && activeCard) {
        activeCard.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
    return () => {};
  });

  const focusGridEl = (x, y, dx, dy, dir, event, trial = 0) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (trial > 10) {
      return;
    }
    const newX = x + dx * dir;
    const newY = y + dy * dir;
    const cont = document.getElementById(`card-grid-${newX}-${newY}`);
    const activeCard = getActiveCard(cont);

    if (activeCard) {
      activeCard.focus();
      return;
    }

    focusGridEl(newX, newY, dx, dy, dir, null, trial + 1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      return;
    }
    if (e.key === "Enter") {
      const cardSelection = new CustomEvent("cardSelected", {
        detail: { selectedCard: id },
        bubbles: true,
      });
      document.dispatchEvent(cardSelection);
      handleClick();
    } else if (e.key === "ArrowRight") {
      focusGridEl(column, row, 1, 0, 1, e);
    } else if (e.key === "ArrowLeft") {
      focusGridEl(column, row, 1, 0, -1, e);
    } else if (e.key === "ArrowUp") {
      focusGridEl(column, row, 0, 1, -1, e);
    } else if (e.key === "ArrowDown") {
      focusGridEl(column, row, 0, 1, 1, e);
    }
  };

  return (
    <div
      className={!disabled ? "tw-pointer-events-none" : ""}
      role="button"
      ref={cardRef}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onFocus={handleOnFocus}
      onBlur={handleOnBlur}
      id={`card-grid-${column}-${row}`}
    >
      {children}
    </div>
  );
}

CardGridEl.propTypes = {
  selectedCard: PropTypes.string,
  selectCard: PropTypes.func.isRequired,
  children: PropTypes.node,
  id: PropTypes.string,
  isCardsOnSide: PropTypes.bool.isRequired,
  isViewAll: PropTypes.bool,
  card: PropTypes.instanceOf(Card),
  column: PropTypes.number,
  row: PropTypes.number,
  disabled: PropTypes.bool,
};

CardGridEl.defaultProps = {
  isViewAll: false,
  disabled: false,
  selectedCard: "",
  children: null,
  id: "",
  card: null,
  column: 0,
  row: 0,
};

const mapStateToProps = (state) => {
  return {
    isCardsOnSide: getCardsIsOnSide(state),
    selectedCard: getSelectedCard(state),
    isViewAll: getCardsIsViewAll(state),
    isCompact: getCardsIsCompact(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  selectCard: (params) => dispatch(setSelectedCard(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CardGridEl);
